import React, { useState, useEffect, useContext } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  CircularProgress,
  alpha,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification
} from '../utils/notificationApi';

function NotificationBell() {
  const { authTokens, backendUrl } = useContext(AuthContext);
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [hasActiveRelationship, setHasActiveRelationship] = useState(false);
  const [activeRelationshipId, setActiveRelationshipId] = useState(null);

  // Fetch notifications on mount and periodically
  useEffect(() => {
    loadNotifications();
    checkActiveRelationship();

    // Poll for new notifications every 1 minute
    const interval = setInterval(loadNotifications, 60000);

    return () => clearInterval(interval);
  }, [authTokens]);

  const checkActiveRelationship = async () => {
    if (!authTokens?.access) return;

    try {
      const response = await fetch(`${backendUrl || 'http://127.0.0.1:8000/'}api/relationship/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authTokens.access}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const isActive = !!data.relationship_id && !data.date_ended;
        setHasActiveRelationship(isActive);
        setActiveRelationshipId(isActive ? data.relationship_id : null);
      }
    } catch (err) {
      console.error('Failed to check active relationship:', err);
    }
  };

  const loadNotifications = async () => {
    if (!authTokens?.access) return;

    try {
      setError(null);
      const data = await fetchNotifications(authTokens.access, { limit: 20 });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      setError('Failed to load notifications');
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    if (notifications.length === 0) {
      setLoading(true);
      loadNotifications().finally(() => setLoading(false));
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      try {
        await markNotificationAsRead(authTokens.access, notification.notification_id);
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev =>
          prev.map(n =>
            n.notification_id === notification.notification_id
              ? { ...n, is_read: true }
              : n
          )
        );
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }

    // Open modal to show full notification
    setSelectedNotification(notification);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedNotification(null);
  };

  const handleModalActionClick = () => {
    // Navigate if there's a link
    if (selectedNotification?.link) {
      handleClose();
      handleModalClose();
      navigate(selectedNotification.link);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsAsRead(authTokens.access);
      setUnreadCount(0);
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleDelete = async (notificationId, event) => {
    event.stopPropagation();

    try {
      await deleteNotification(authTokens.access, notificationId);

      // Update local state
      const deletedNotification = notifications.find(n => n.notification_id === notificationId);
      if (deletedNotification && !deletedNotification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      setNotifications(prev =>
        prev.filter(n => n.notification_id !== notificationId)
      );
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const getNotificationTypeColor = (type) => {
    switch (type) {
      case 'PARTNER_INVITATION':
        return '#C4856A';
      case 'RELATIONSHIP_UPDATE':
        return '#7B6B8D';
      case 'SYSTEM_MESSAGE':
        return '#90CAF9';
      case 'DIRECT_MESSAGE':
        return '#A5D6A7';
      case 'RELATIONSHIP_UNLINKED':
        return '#FFB74D';
      default:
        return '#E0E0E0';
    }
  };

  const formatNotificationType = (type) => {
    return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          color: '#666',
          '&:hover': {
            backgroundColor: alpha('#7B6B8D', 0.08),
            color: '#7B6B8D'
          }
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 400,
            maxHeight: 600,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }
        }}
      >
        <Box>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #E8E8E8'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Button
                size="small"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAllRead}
                sx={{
                  color: '#7B6B8D',
                  '&:hover': {
                    backgroundColor: alpha('#7B6B8D', 0.08)
                  }
                }}
              >
                Mark all read
              </Button>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ maxHeight: 480, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={40} sx={{ color: '#7B6B8D' }} />
              </Box>
            ) : error ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
                <Button
                  onClick={loadNotifications}
                  sx={{ mt: 1, color: '#7B6B8D' }}
                >
                  Retry
                </Button>
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <NotificationsIcon
                  sx={{ fontSize: 48, color: '#E0E0E0', mb: 1 }}
                />
                <Typography color="textSecondary">
                  No notifications yet
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {notifications.map((notification, index) => (
                  <React.Fragment key={notification.notification_id}>
                    {index > 0 && <Divider />}
                    <ListItem
                      button
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        py: 2,
                        px: 2,
                        backgroundColor: notification.is_read
                          ? 'transparent'
                          : alpha('#7B6B8D', 0.05),
                        '&:hover': {
                          backgroundColor: alpha('#7B6B8D', 0.1)
                        },
                        cursor: 'pointer'
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            mb: 0.5
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: notification.is_read ? 400 : 600,
                              flex: 1,
                              mr: 1
                            }}
                          >
                            {notification.title || formatNotificationType(notification.notification_type)}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => handleDelete(notification.notification_id, e)}
                            sx={{
                              ml: 1,
                              '&:hover': { color: '#F44336' }
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {notification.text}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Chip
                            label={formatNotificationType(notification.notification_type)}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: alpha(
                                getNotificationTypeColor(notification.notification_type),
                                0.15
                              ),
                              color: getNotificationTypeColor(notification.notification_type),
                              fontWeight: 500
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {formatTimestamp(notification.time_sent)}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Popover>

      {/* Notification Detail Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        {selectedNotification && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pb: 1
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {selectedNotification.title || formatNotificationType(selectedNotification.notification_type)}
                </Typography>
                <Chip
                  label={formatNotificationType(selectedNotification.notification_type)}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '0.75rem',
                    backgroundColor: alpha(
                      getNotificationTypeColor(selectedNotification.notification_type),
                      0.15
                    ),
                    color: getNotificationTypeColor(selectedNotification.notification_type),
                    fontWeight: 500
                  }}
                />
              </Box>
              <IconButton
                onClick={handleModalClose}
                sx={{ ml: 1 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {selectedNotification.text}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block', mt: 2 }}
              >
                {formatTimestamp(selectedNotification.time_sent)}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleModalClose}
                sx={{ color: '#666' }}
              >
                Close
              </Button>
              {selectedNotification.link && (
                <Button
                  onClick={handleModalActionClick}
                  variant="contained"
                  sx={{
                    backgroundColor: '#7B6B8D',
                    '&:hover': {
                      backgroundColor: '#A394C3'
                    }
                  }}
                >
                  View
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

    </>
  );
}

export default NotificationBell;
